import { motion } from "framer-motion";
import moment from "moment";
import React, { useContext, useState } from "react";
import supabase from "./../Config/Supabase";
import DataContext from "../Context/DAtaContext";
import { toast } from "react-toastify";
import { Loader, X, User, CheckCircle2, AlertCircle } from "lucide-react";

// Date formatter component
const formatDate = (dateString) => {
  return moment(dateString).format("MMMM D, YYYY [at] h:mm:ss A");
};

const FarmerDetails = ({
  farmer,
  FPackage,
  Vano,
  zone,
  FPayment,
  FBundle,
  FVillage,
  setOpen,
}) => {
  const [checkoutResults, setCheckoutResults] = useState({});
  const { fetchFarmerBundle } = useContext(DataContext);
  const [loading, setLoading] = useState(false);
  const [loadings, setLoadings] = useState(false);
  const [packageId, setPackageId] = useState(" ");

  // Logic to calculate how many products the farmer can claim across all packages based on the total amount paid
  const calculateClaimAcrossPackages = (packages, amountPaid, FBundles) => {
    let claimResults = [];
    let remainingAmount = amountPaid;

    const sortedPackages = [...packages].sort((a, b) => b.price - a.price);

    for (let pkg of sortedPackages) {
      const unitPrice = pkg.price;
      const maxClaimable = Math.floor(amountPaid / unitPrice);

      if (maxClaimable > 0) {
        const totalCost = maxClaimable * unitPrice;
        claimResults.push({
          ...FBundles,
          name: pkg.name,
          claimed: maxClaimable,
          unitPrice: unitPrice,
          totalCost: totalCost,
          remainingAmount: remainingAmount - totalCost,
        });
      }
    }

    return claimResults;
  };

  // Handle the Checkout button click
  const handleCheckout = (packages, amountPaid, index) => {
    const claimDetails = calculateClaimAcrossPackages(
      packages,
      amountPaid,
      FBundle[index],
    );
    setCheckoutResults((prevResults) => ({
      ...prevResults,
      [index]: claimDetails,
    }));
  };

  // Handle selecting a package to checkout
  const handlePackageSelect = (pkge) => {
    setLoadings(true);
    handleDelivery(pkge);
  };

  const totalSumPay = FPayment?.reduce(
    (sum, payment) => sum + payment.amount,
    0,
  );

  // Summation logic
  const cprices = FPackage?.map(
    (dt, index) => dt.price * FBundle[index]?.amount,
  );

  const TotalPrice = cprices?.reduce(
    (accumulator, currentValue) => accumulator + currentValue,
    0,
  );

  // Mark product delivery function
  const handleDelivery = (pakage) => {
    setLoading(true);
    if (pakage?.package_delivered_at !== null) {
      handleSubmited(pakage.id);
      setPackageId(pakage.id);
    } else {
      handleSubmit(pakage.id);
      setPackageId(pakage.id);
    }
  };

  // submit function for checked package
  const handleSubmit = async (PakageId) => {
    const { error } = await supabase
      .from("farmer_bundle")
      .update({
        package_delivered_at: new Date().toISOString(),
      })
      .eq("id", PakageId)
      .select();

    if (error) {
      setLoading(false);
      setLoadings(false);
      toast.error("Error checking the package");
    } else {
      await insertDeliveryRecord(PakageId);
      fetchFarmerBundle();
      toast.success("Package delivery marked as checked successfully");
      setLoading(false);
      setLoadings(false);
    }
  };

  // handle send delivery data to db
  const insertDeliveryRecord = async (bundleId) => {
    const bundle = FBundle.find((b) => b.id === bundleId);
    const pkg = FPackage.find((p) => p.id === bundle.bundle_id);

    if (!bundle || !pkg) return;

    const { error } = await supabase.from("delivery").insert([
      {
        farmer_id: farmer[0]?.id,
        bundle_id: bundle?.id,
        farmer_name: farmer[0]?.name,
        phone_number: farmer[0]?.phone,
        package_name: `${bundle?.amount} ${pkg.name}`,
        amount: pkg.price * bundle?.amount,
        delivery_date: new Date().toISOString(),
        synced: false,
      },
    ]);

    if (error) {
      toast.error("Error inserting the delivery data");
      console.error("Failed to insert into delivery table", error);
    }

    toast.success("Inserted successfully");
  };

  const handleUncheckDelivery = async (packageId) => {
    const { error } = await supabase
      .from("delivery")
      .delete()
      .eq("bundle_id", packageId);

    if (error) {
      console.log("Error deleting delivery:", error);
      toast.error("Error deleting delivery record");
      return error;
    }

    return null;
  };

  const handleSubmited = async (packageId) => {
    setLoading(true);

    try {
      // 1. Update farmer bundle first
      const { error: updateError } = await supabase
        .from("farmer_bundle")
        .update({ package_delivered_at: null })
        .eq("id", packageId);

      if (updateError) {
        throw new Error("Failed to update farmer bundle");
      }

      // 2. Then delete delivery record
      const deleteError = await handleUncheckDelivery(packageId);

      if (deleteError) {
        throw new Error("Failed to delete delivery record");
      }

      // 3. Refresh UI only if both succeed
      await fetchFarmerBundle();

      toast.success("Package successfully unchecked");
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Error unchecking package");
    } finally {
      setLoading(false);
    }
  };

  const remainingBalance = TotalPrice - totalSumPay;

  return (
    <div className="h-full px-3 py-4 overflow-y-auto text-gray-900 bg-white rounded-lg shadow-xl dark:bg-gray-800 md:px-6 md:py-4">
      {/* Header */}
      <div className="flex items-center justify-between pb-2 mb-6 border-b-2 border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-green-100 rounded-lg dark:bg-green-900/30">
            <User className="w-4 h-4 text-green-600 dark:text-green-400 md:w-4 md:h-4" />
          </div>
          <h3 className="text-sm font-bold text-gray-800 dark:text-gray-100 md:text-xl">
            Farmer Details
          </h3>
        </div>
        <button
          onClick={() => setOpen(false)}
          className="text-gray-500 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Farmer Info */}
      <motion.div
        className="grid grid-cols-1 gap-6 mb-8 lg:grid-cols-2"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Personal Information Card */}
        <div className="overflow-hidden bg-gray-50 dark:bg-gray-900/50 rounded-xl">
          <div className="px-4 py-3 bg-gray-200 dark:bg-slate-900">
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 md:text-base">
              Personal Information
            </h4>
          </div>
          <table className="w-full dark:text-gray-300">
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              <tr className="transition-colors hover:bg-gray-100 dark:hover:bg-gray-800/50">
                <th className="px-4 py-3 text-xs font-semibold text-left text-gray-600 bg-gray-100 dark:bg-slate-900 dark:text-gray-400 md:text-sm">
                  ID
                </th>
                <td className="px-4 py-3 text-xs font-medium dark:bg-gray-700/30 md:text-sm">
                  {farmer[0]?.id}
                </td>
              </tr>
              <tr className="transition-colors hover:bg-gray-100 dark:hover:bg-gray-800/50">
                <th className="px-4 py-3 text-xs font-semibold text-left text-gray-600 bg-gray-100 dark:bg-slate-900 dark:text-gray-400 md:text-sm">
                  Name
                </th>
                <td className="px-4 py-3 text-xs font-medium capitalize dark:bg-gray-700/30 md:text-sm">
                  {farmer[0]?.name}
                </td>
              </tr>
              <tr className="transition-colors hover:bg-gray-100 dark:hover:bg-gray-800/50">
                <th className="px-4 py-3 text-xs font-semibold text-left text-gray-600 bg-gray-100 dark:bg-slate-900 dark:text-gray-400 md:text-sm">
                  Gender
                </th>
                <td className="px-4 py-3 text-xs font-medium dark:bg-gray-700/30 md:text-sm">
                  {farmer[0]?.gender}
                </td>
              </tr>
              <tr className="transition-colors hover:bg-gray-100 dark:hover:bg-gray-800/50">
                <th className="px-4 py-3 text-xs font-semibold text-left text-gray-600 bg-gray-100 dark:bg-slate-900 dark:text-gray-400 md:text-sm">
                  Telephone
                </th>
                <td className="px-4 py-3 text-xs font-medium dark:bg-gray-700/30 md:text-sm">
                  {farmer[0]?.phone}
                </td>
              </tr>
              <tr className="transition-colors hover:bg-gray-100 dark:hover:bg-gray-800/50">
                <th className="px-4 py-3 text-xs font-semibold text-left text-gray-600 bg-gray-100 dark:bg-slate-900 dark:text-gray-400 md:text-sm">
                  Household Members
                </th>
                <td className="px-4 py-3 text-xs font-medium dark:bg-gray-700/30 md:text-sm">
                  {farmer[0]?.number_of_people_in_house_hold}
                </td>
              </tr>
              <tr className="transition-colors hover:bg-gray-100 dark:hover:bg-gray-800/50">
                <th className="px-4 py-3 text-xs font-semibold text-left text-gray-600 bg-gray-100 dark:bg-slate-900 dark:text-gray-400 md:text-sm">
                  Breastfeeding Children
                </th>
                <td className="px-4 py-3 text-xs font-medium dark:bg-gray-700/30 md:text-sm">
                  {farmer[0]?.number_of_breast_feeding_children}
                </td>
              </tr>
              <tr className="transition-colors hover:bg-gray-100 dark:hover:bg-gray-800/50">
                <th className="px-4 py-3 text-xs font-semibold text-left text-gray-600 bg-gray-100 dark:bg-slate-900 dark:text-gray-400 md:text-sm">
                  Children Under 5
                </th>
                <td className="px-4 py-3 text-xs font-medium dark:bg-gray-700/30 md:text-sm">
                  {farmer[0]?.number_of_children_under_5}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Financial & Location Information */}
        <div className="flex flex-col gap-6">
          {/* Financial Summary Card */}
          <div className="overflow-hidden bg-gray-50 dark:bg-gray-900/50 rounded-xl">
            <div className="px-4 py-3 bg-gray-200 dark:bg-slate-900">
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 md:text-base">
                Financial Summary
              </h4>
            </div>
            <table className="w-full dark:text-gray-300">
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                <tr className="transition-colors hover:bg-gray-100 dark:hover:bg-gray-800/50">
                  <th className="px-4 py-3 text-xs font-semibold text-left text-gray-600 bg-gray-100 dark:bg-slate-900 dark:text-gray-400 md:text-sm">
                    Total Price
                  </th>
                  <td className="px-4 py-3 text-xs font-bold text-gray-900 dark:bg-gray-700/30 dark:text-white md:text-sm">
                    {TotalPrice?.toLocaleString()} TZS
                  </td>
                </tr>
                <tr className="transition-colors hover:bg-gray-100 dark:hover:bg-gray-800/50">
                  <th className="px-4 py-3 text-xs font-semibold text-left text-gray-600 bg-gray-100 dark:bg-slate-900 dark:text-gray-400 md:text-sm">
                    Amount Paid
                  </th>
                  <td className="px-4 py-3 text-xs font-bold text-green-700 dark:bg-gray-700/30 dark:text-green-400 md:text-sm">
                    {totalSumPay?.toLocaleString()} TZS
                  </td>
                </tr>
                <tr className="transition-colors">
                  <th className="px-4 py-3 text-xs font-semibold text-left text-gray-600 bg-gray-100 dark:bg-slate-900 dark:text-gray-400 md:text-sm">
                    Balance
                  </th>
                  <td
                    className={`px-4 py-3 text-xs md:text-sm font-bold ${
                      remainingBalance > 0
                        ? "bg-red-600 text-white"
                        : remainingBalance === 0
                          ? "bg-green-600 text-white"
                          : "bg-yellow-600 text-white"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {remainingBalance > 0 ? (
                        <AlertCircle className="w-4 h-4" />
                      ) : (
                        <CheckCircle2 className="w-4 h-4" />
                      )}
                      <span>{remainingBalance?.toLocaleString()} TZS</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Location Information Card */}
          <div className="overflow-hidden bg-gray-50 dark:bg-gray-900/50 rounded-xl">
            <div className="px-4 py-3 bg-gray-200 dark:bg-slate-900">
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 md:text-base">
                Location Details
              </h4>
            </div>
            <table className="w-full dark:text-gray-300">
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                <tr className="transition-colors hover:bg-gray-100 dark:hover:bg-gray-800/50">
                  <th className="px-4 py-3 text-xs font-semibold text-left text-gray-600 bg-gray-100 dark:bg-slate-900 dark:text-gray-400 md:text-sm">
                    Zone
                  </th>
                  <td className="px-4 py-3 text-xs font-medium dark:bg-gray-700/30 md:text-sm">
                    {zone[0]?.name}
                  </td>
                </tr>
                <tr className="transition-colors hover:bg-gray-100 dark:hover:bg-gray-800/50">
                  <th className="px-4 py-3 text-xs font-semibold text-left text-gray-600 bg-gray-100 dark:bg-slate-900 dark:text-gray-400 md:text-sm">
                    Village
                  </th>
                  <td className="px-4 py-3 text-xs font-medium dark:bg-gray-700/30 md:text-sm">
                    {FVillage[0]?.name}
                  </td>
                </tr>
                <tr className="transition-colors hover:bg-gray-100 dark:hover:bg-gray-800/50">
                  <th className="px-4 py-3 text-xs font-semibold text-left text-gray-600 bg-gray-100 dark:bg-slate-900 dark:text-gray-400 md:text-sm">
                    VANO
                  </th>
                  <td className="px-4 py-3 text-xs font-medium dark:bg-gray-700/30 md:text-sm">
                    {Vano[0]?.name}
                  </td>
                </tr>
                {farmer[0]?.group_name && (
                  <tr className="transition-colors hover:bg-gray-100 dark:hover:bg-gray-800/50">
                    <th className="px-4 py-3 text-xs font-semibold text-left text-gray-600 bg-gray-100 dark:bg-slate-900 dark:text-gray-400 md:text-sm">
                      Group Name
                    </th>
                    <td className="px-4 py-3 text-xs font-medium dark:bg-gray-700/30 md:text-sm">
                      {farmer[0]?.group_name}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </motion.div>

      {/* Payment History */}
      <motion.div
        className="mb-8"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.5 }}
      >
        <div className="flex items-center gap-3 pb-3 mb-4 border-b-2 border-gray-200 dark:border-gray-700">
          <h3 className="text-base font-bold text-gray-800 dark:text-gray-100 md:text-lg">
            Payment History
          </h3>
        </div>
        <div className="overflow-hidden rounded">
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white dark:bg-slate-900">
              <thead>
                <tr className="text-left bg-gray-300 dark:bg-green-600">
                  <th className="px-4 py-3 text-xs font-semibold text-gray-700 dark:text-white md:text-sm">
                    Phone Number
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-700 dark:text-white md:text-sm">
                    Amount
                  </th>
                  <th className="hidden px-4 py-3 text-xs font-semibold text-gray-700 md:table-cell dark:text-white md:text-sm">
                    Payment Method
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-700 dark:text-white md:text-sm">
                    Transaction Date
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700 dark:text-gray-300">
                {FPayment?.map((payment, index) => (
                  <tr
                    key={payment.id + index}
                    className="transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50"
                  >
                    <td className="px-4 py-3 text-xs font-medium md:text-sm">
                      {farmer[0]?.phone}
                    </td>
                    <td className="px-4 py-3 text-xs font-bold text-green-600 dark:text-green-400 md:text-sm">
                      {payment?.amount?.toLocaleString()} TZS
                    </td>
                    <td className="hidden px-4 py-3 text-xs font-medium md:table-cell md:text-sm">
                      <span className="px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded-full dark:bg-gray-700 dark:text-gray-300">
                        {payment?.payment_method}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs font-medium text-gray-600 dark:text-gray-400 md:text-sm">
                      {formatDate(payment?.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </motion.div>

      {/* Nutritional Packages */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6, duration: 0.5 }}
      >
        <div className="flex items-center gap-3 pb-3 mb-4 border-b-2 border-gray-200 dark:border-gray-700">
          <h3 className="text-base font-bold text-gray-800 dark:text-gray-100 md:text-lg">
            Nutritional Packages
          </h3>
        </div>
        <div className="overflow-hidden rounded">
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white dark:bg-slate-900">
              <thead>
                <tr className="text-left bg-gray-300 dark:bg-green-600">
                  <th className="px-4 py-3 text-xs font-semibold text-gray-700 dark:text-white md:text-sm">
                    Name
                  </th>
                  <th className="hidden px-4 py-3 text-xs font-semibold text-gray-700 md:table-cell dark:text-white md:text-sm">
                    Quantity
                  </th>
                  <th className="hidden px-4 py-3 text-xs font-semibold text-gray-700 md:table-cell dark:text-white md:text-sm">
                    Unit Price
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-700 dark:text-white md:text-sm">
                    Total Price
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-700 dark:text-white md:text-sm">
                    Delivery Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700 dark:text-gray-300">
                {Array.isArray(FPackage) && FPackage.length > 0 ? (
                  FPackage?.map((pkg, index) => (
                    <React.Fragment key={index + pkg?.name}>
                      <tr className="transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <td className="px-4 py-3 text-xs font-medium md:text-sm">
                          {pkg?.name}
                        </td>
                        <td className="hidden px-4 py-3 text-xs font-medium md:table-cell md:text-sm">
                          <span className="px-2 py-1 text-xs font-bold text-gray-700 bg-gray-100 rounded-full dark:bg-gray-700 dark:text-gray-300">
                            {FBundle[index]?.amount}
                          </span>
                        </td>
                        <td className="hidden px-4 py-3 text-xs font-medium md:table-cell md:text-sm">
                          {pkg?.price?.toLocaleString()} TZS
                        </td>
                        <td className="px-4 py-3 text-xs font-bold text-green-600 dark:text-green-400 md:text-sm">
                          {(
                            pkg?.price * FBundle[index]?.amount
                          )?.toLocaleString()}{" "}
                          TZS
                        </td>
                        <td className="px-4 py-3 text-xs md:text-sm">
                          {TotalPrice - totalSumPay <= 0 ||
                          FBundle[index]?.package_delivered_at !== null ? (
                            <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center">
                              {loading && packageId === FBundle[index]?.id ? (
                                <div className="flex items-center justify-center">
                                  <Loader className="w-5 h-5 text-green-600 animate-spin" />
                                </div>
                              ) : (
                                <>
                                  <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      onClick={() =>
                                        handleDelivery(FBundle[index])
                                      }
                                      checked={
                                        FBundle[index]?.package_delivered_at !==
                                        null
                                      }
                                      className="w-4 h-4 text-green-600 border-gray-300 rounded form-checkbox focus:ring-green-500"
                                      readOnly
                                    />
                                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                      {FBundle[index]?.package_delivered_at !==
                                      null
                                        ? "Delivered"
                                        : "Mark as delivered"}
                                    </span>
                                  </label>
                                  {FBundle[index]?.package_delivered_at !==
                                    null && (
                                    <p className="hidden text-xs text-gray-500 md:block dark:text-gray-400">
                                      {formatDate(
                                        FBundle[index]?.package_delivered_at,
                                      )}
                                    </p>
                                  )}
                                </>
                              )}
                            </div>
                          ) : (
                            <button
                              className="px-4 py-2 text-xs font-semibold text-white transition-colors bg-green-600 rounded-lg hover:bg-green-700 sm:text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                              onClick={() =>
                                handleCheckout(FPackage, totalSumPay, index)
                              }
                            >
                              Checkout
                            </button>
                          )}
                        </td>
                      </tr>

                      {/* Display the detailed checkout summary for this package row */}
                      {checkoutResults[index] && (
                        <tr>
                          <td colSpan="5" className="p-0">
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              className="p-4 bg-gray-100 dark:bg-gray-700/50"
                            >
                              <div className="flex items-center justify-between mb-4">
                                <h4 className="text-sm font-bold text-gray-800 dark:text-gray-200 md:text-base">
                                  Available Checkout Options
                                </h4>
                                <button
                                  onClick={() => setCheckoutResults("")}
                                  className="p-1 text-gray-500 transition-colors rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>

                              <div className="overflow-x-auto rounded-lg">
                                <table className="w-full bg-white dark:bg-gray-800">
                                  <thead>
                                    <tr className="text-left bg-gray-200 dark:bg-gray-900">
                                      <th className="px-3 py-2 text-xs font-semibold text-gray-700 dark:text-gray-300 md:text-sm">
                                        Items Claimed
                                      </th>
                                      <th className="px-3 py-2 text-xs font-semibold text-gray-700 dark:text-gray-300 md:text-sm">
                                        Unit Price
                                      </th>
                                      <th className="px-3 py-2 text-xs font-semibold text-gray-700 dark:text-gray-300 md:text-sm">
                                        Total Cost
                                      </th>
                                      <th className="px-3 py-2 text-xs font-semibold text-gray-700 dark:text-gray-300 md:text-sm">
                                        Remaining Balance
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                    {checkoutResults[index].map((result, i) => (
                                      <tr
                                        key={i}
                                        className="transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50"
                                      >
                                        <td className="px-3 py-3 text-xs font-medium dark:text-gray-300 md:text-sm">
                                          <span className="font-bold text-green-600 dark:text-green-400">
                                            {result.claimed}
                                          </span>{" "}
                                          × {result.name}
                                        </td>
                                        <td className="px-3 py-3 text-xs font-medium dark:text-gray-300 md:text-sm">
                                          {result.unitPrice?.toLocaleString()}{" "}
                                          TZS
                                        </td>
                                        <td className="px-3 py-3 text-xs font-bold text-green-600 dark:text-green-400 md:text-sm">
                                          {result.totalCost?.toLocaleString()}{" "}
                                          TZS
                                        </td>
                                        <td className="px-3 py-3">
                                          <div className="flex items-center justify-between gap-3">
                                            <span className="text-xs font-medium dark:text-gray-300 md:text-sm">
                                              {result.remainingAmount?.toLocaleString()}{" "}
                                              TZS
                                            </span>
                                            {result.remainingAmount <= 0 && (
                                              <button
                                                className="px-3 py-1.5 text-xs font-semibold text-white transition-colors bg-blue-500 rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                                onClick={() =>
                                                  handlePackageSelect(result)
                                                }
                                                disabled={loadings}
                                              >
                                                {loadings ? (
                                                  <Loader className="w-4 h-4 animate-spin" />
                                                ) : (
                                                  "Select"
                                                )}
                                              </button>
                                            )}
                                          </div>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </motion.div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="5"
                      className="px-4 py-8 text-sm text-center text-gray-500 dark:text-gray-400"
                    >
                      No packages available for this farmer.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default FarmerDetails;
